import React, { useEffect, useState } from 'react';
import { Stack, Form, Tab, Tabs, Row } from 'react-bootstrap';

import { useSearchParams } from 'react-router-dom';
import {
	useFollowCompanyMutation,
	useFollowHashtagMutation,
	useGetFollowingCompanyQuery,
	useGetFollowingHashtagQuery,
	useGetBlockListQuery,
} from 'services';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore, { updateUserInfo } from 'store/auth';
import useSkeleton from 'hooks/useSkeleton';
import PaginationBox from 'components/PaginationBox';
import FollowingHashtags from 'components/Followings/Hashtags/FollowingHashtags';
import FollowingCompanies from 'components/Followings/Companies/FollowingCompanies';
import MembersSkeleton from 'components/Common/Skeleton/MembersSkeleton';
import { dataLimits, USER_TYPES } from 'constants/index';
import useCommonStore, { setModalConfig } from 'store/common';
import BlockedUsers from 'components/Connections/BlockedUsers/BlockedUser';
import { decreaseFollowing, increaseFollowing } from 'utils';
import useUserStore from 'store/user';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

const FollowingsList: React.FC = () => {
	const { translate } = useTranslate();
	const { followedHashTags = [], setFollowedHashTags }: Record<string, any> = useUserStore(
		(state) => state,
	);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const delayedLoading = useSkeleton(isLoading);
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTabKey: any = searchParams.get('activeTab') ?? 'company';
	const userId = searchParams.get('userId') ?? '';
	const [refetch, setRefetch] = useState<boolean>(false);
	const [startSearch, setStartSearch] = useState<boolean>(false);

	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const [userData, setUserData] = useState({
		currentPage: 1,
		limit: dataLimits.limits,
		order: 'DESC',
		connectionType: activeTabKey,
		keyword: searchParams.get('value') ?? '',
		id: userId || user?.userId,
	});

	const { data: { docs: hashTagData = [], meta = {} } = {} }: any =
		useGetFollowingHashtagQuery(userData);
	const {
		data: { docs: FollowingsCompanyData = [], meta: followingCompaniesMeta = {} } = {},
	}: any = useGetFollowingCompanyQuery(userData);

	// New query for blocked profiles
	const { data: blockedProfilesData = [] }: any = useGetBlockListQuery(userData);

	const { mutateAsync: followCompanyMutation } = useFollowCompanyMutation();
	const { mutateAsync: followHashtagMutation } = useFollowHashtagMutation();

	useEffect(() => {
		if (followingCompaniesMeta?.totalDocs) {
			updateUserInfo({ followings: followingCompaniesMeta?.totalDocs || 0 });
		}
	}, [followingCompaniesMeta?.totalDocs]);

	useEffect(() => {
		setIsLoading(true);
		if (activeTabKey === 'hashtag') {
			queryClient.invalidateQueries({ queryKey: [`following-hashtags`] }).finally(() => {
				setIsLoading(false);
			});
		} else if (activeTabKey === 'blockedProfiles') {
			queryClient.invalidateQueries({ queryKey: [`get-block-users`] }).finally(() => {
				setIsLoading(false);
			});
		} else {
			queryClient
				.invalidateQueries({
					queryKey: [`following-company`],
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
		window.scrollTo({
			top: 0,
			behavior: 'instant',
		});
	}, [userData.currentPage, startSearch, refetch, activeTabKey, userId]);

	const handleCurrentPage = (e: any) => {
		setUserData((prev) => ({
			...prev,
			currentPage: e,
		}));
	};

	const handleTabChange = (e: any) => {
		setUserData((prev) => ({
			...prev,
			connectionType: e,
			currentPage: 1,
		}));
		setSearchParams({ activeTab: e, value: userData?.keyword ?? '', userId: userId || '' });
	};

	const handleSearch = (e: any) => {
		if (e.keyCode === 13 && e.target.value) {
			searchParams.set('value', e.target.value.trim());
			setSearchParams(searchParams);
			setStartSearch((prev) => !prev);
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value.trim(),
			}));
		}
	};

	const handleOnChange = (e: any) => {
		setSearchParams({ activeTab: userData?.connectionType, value: e.target.value });
		if (e?.target?.value.length === 0) {
			setStartSearch((prev) => !prev);
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value,
			}));
		} else {
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value,
			}));
		}
	};

	const unFollowAccountModal = (id: any) => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			id,
			onClick: () => unFollow(id),
			data: {
				title: ['hashtag']?.includes(userData.connectionType)
					? translate('Common.wantUnFollowHash')
					: translate('Common.wantUnFollowCompany'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const refetchBlockedListData = (type: string) => {
		setUserData((prev) => ({
			...prev,
			currentPage: 1,
			connectionType: type,
		}));

		if (userData?.currentPage === 1) {
			queryClient.invalidateQueries({ queryKey: [`get-block-users`] });
		}
	};

	const unFollow = (id: any) => {
		if (!['hashtag']?.includes(userData.connectionType)) {
			decreaseFollowing(user?.followings);
		}
		const payload = ['hashtag']?.includes(userData.connectionType)
			? Number(id)
			: { id: Number(id) };
		const apiCalling = ['hashtag']?.includes(userData.connectionType)
			? followHashtagMutation
			: followCompanyMutation;
		apiCalling(payload)
			.then((res: any) => {
				if (res?.success) {
					setRefetch((prev) => !prev);
					if (!['hashtag']?.includes(userData.connectionType)) {
						const hashTagData = followedHashTags.filter((res: any) => res?.id !== id);
						setFollowedHashTags(hashTagData);
					}
				} else {
					if (!['hashtag']?.includes(userData.connectionType)) {
						increaseFollowing(user?.followings);
					}
					console.error('Error handling connection request');
				}
			})
			.catch((err: any) => {
				console.error('Error:', err);
				if (!['hashtag']?.includes(userData.connectionType)) {
					increaseFollowing(user?.followings);
				}
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	return (
		<div className="white-box bg-white">
			<Stack className="gap-3 gap-md-4">
				<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
					<div className="list-heading">
						<h4 className="fw-bold">{translate('Common.followingsHashtags')}</h4>
					</div>
					<Form.Group className="user-search position-relative w-100">
						<i className="ri-search-line position-absolute"></i>
						<Form.Control
							className="rounded-pill"
							type="search"
							placeholder={translate('PlaceHolder.searchbyCompanyHashtags')}
							onKeyDown={handleSearch}
							onChange={handleOnChange}
							value={userData.keyword ?? ''}
						/>
					</Form.Group>
				</div>
				<div className="connections-tab">
					<Tabs
						defaultActiveKey={activeTabKey}
						id="uncontrolled-tab-example"
						onSelect={handleTabChange}
						activeKey={activeTabKey}
					>
						<Tab eventKey="company" title={translate('Social.company')}>
							<div>
								<h6 className="mb-3">
									{translate('Sidebar.followings')}{' '}
									<span className="fw-bold">({followingCompaniesMeta?.totalDocs})</span>
								</h6>
								<Row className="connections-row">
									{delayedLoading ? (
										<MembersSkeleton />
									) : (
										<FollowingCompanies
											FollowingsCompanyData={FollowingsCompanyData}
											unFollowCompany={unFollowAccountModal}
										/>
									)}
								</Row>
							</div>
						</Tab>
						<Tab eventKey="hashtag" title={translate('Social.hashtags')}>
							<div>
								<h6 className="mb-3">
									{translate('Sidebar.followings')}{' '}
									<span className="fw-bold">({meta?.totalDocs})</span>
								</h6>
								<Row className="connections-row">
									{delayedLoading ? (
										<MembersSkeleton />
									) : (
										<FollowingHashtags
											hashTagData={hashTagData}
											unFollowHashTag={unFollowAccountModal}
										/>
									)}
								</Row>
							</div>
						</Tab>
						{user?.userType === USER_TYPES.COMPANY && blockedProfilesData && (
							<Tab eventKey="blockedProfiles" title={translate('Social.blockedProfiles')}>
								<div>
									<Row className="connections-row">
										{delayedLoading ? (
											<MembersSkeleton />
										) : (
											<BlockedUsers
												connectionData={blockedProfilesData}
												refetchBlockedListData={refetchBlockedListData}
												otherData={userData}
												handleCurrentPage={handleCurrentPage}
											/>
										)}
									</Row>
								</div>
							</Tab>
						)}
					</Tabs>
				</div>
			</Stack>
			{['hashtag', 'blockedProfiles'].includes(userData.connectionType) ? (
				<PaginationBox
					totalData={
						userData.connectionType === 'blockedProfiles'
							? blockedProfilesData?.meta?.totalDocs
							: meta?.totalDocs
					}
					limit={
						userData.connectionType === 'blockedProfiles'
							? blockedProfilesData?.meta?.limit
							: meta?.limit
					}
					page={userData.currentPage}
					setCurrentPage={handleCurrentPage}
				/>
			) : (
				<PaginationBox
					totalData={followingCompaniesMeta?.totalDocs}
					limit={followingCompaniesMeta?.limit}
					page={userData.currentPage}
					setCurrentPage={handleCurrentPage}
				/>
			)}
		</div>
	);
};

export default FollowingsList;
